import { useEffect, useState } from 'react';
import { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  dialogClasses,
} from '@mui/material';
import { post } from 'api';
import { get } from 'api';
import { url } from 'config/config';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';
import IconifyIcon from 'components/base/IconifyIcon';

const unitTypes = ['Nos', 'Pair', 'Meter', 'Pkt', 'Gram', 'Kg', 'Liter', 'Piece', 'Set'];

const validationSchema = yup.object({
  type: yup.string().required('Product Type is required'),
  productName: yup.string().required('Product name is required'),
  hsnCode: yup.string().required('HSN code is required'),
  minimumQnt: yup
    .number()
    .typeError('Enter valid quantity')
    .required('Minimum quantity is required')
    .min(0, 'Quantity must be positive'),
  currentQnt: yup
    .number()
    .typeError('Enter valid stock')
    .min(0, 'Stock must be positive')
    .nullable(),
  manufactureName: yup.string().required('manufactureName name required'),
  vendorName: yup.string().required('vendorName name required'),
  unitType: yup.string().required('Select a unit type'),
  productImage: yup.mixed().required('Product productImage required'),
});

const dialogSchema = yup.object({
  name: yup.string().trim().required('Product type is required').max(40, 'Max 40 characters'),
});

const CreateInventory = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { state } = useLocation();
  const isEditMode = !!state;
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      type: '',
      productName: '',
      hsnCode: '',
      minimumQnt: 0,
      currentQnt: 0,
      manufactureName: '',
      vendorName: '',
      unitType: '',
      productImage: null,
    },
  });
  const selectedUnit = watch('unitType');

  useEffect(() => {
    if (state) {
      reset({
        type: state.type?._id || '',
        productName: state.productName || '',
        hsnCode: state.hsnCode || '',
        minimumQnt: state.minimumQnt || 0,
        currentQnt: state.currentQnt || 0,
        manufactureName: state.manufactureName || '',
        vendorName: state.vendorName._id || '',
        unitType: state.unitType || '',
        productImage: state.productImage, // handled separately
      });

      // Set preview to show existing image
      if (state.productImage) {
        setPreview(`${url}${state.productImage}`);
      }
    }
  }, [state, reset]);

  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const fetchVendors = async () => {
    try {
      const response = await post('users/dropdown_vendor', {});
      if (response.status == 200) {
        setVendors(response.data);
      } else {
        enqueueSnackbar(response.message || 'Error while Fetching Vendor dropdown.', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const fetchType = async () => {
    try {
      const response = await get('users/dropdown_productType');
      if (response.status == 200) {
        setTypes(response.data);
      } else {
        enqueueSnackbar(response.message || 'Error while Fetching Product Type.', {
          variant: 'error',
        });
      }
    } catch (error) {
      console.log('error', error);
      enqueueSnackbar(error.message || 'Error while Fetching Product Type.', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchType();
    fetchVendors();
  }, []);

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === 'minimumQnt') value = Number(value);
        if (key === 'currentQnt') {
          if (value !== '' && value !== null && value !== undefined) {
            value = Number(value);
          } else {
            return;
          }
        }
        formData.append(key, value);
      });
      let response;
      if (isEditMode) {
        // Update existing product
        formData.append('_id', state._id);
        response = await post(`users/update_product`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        // Create new product
        response = await post('users/create_product', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      if (response.status == 200) {
        isEditMode && navigate('/inventory-list');
        enqueueSnackbar(
          response.message ||
            (isEditMode ? 'Product Updated Successfully' : 'Product Created Successfully'),
          {
            variant: 'success',
          },
        );
        fetchType();
      } else {
        enqueueSnackbar(response.message || 'Error while Saving Product', {
          variant: 'error',
        });
      }
      reset();
      setPreview(null);
    } catch (error) {
      console.error('error', error);
      enqueueSnackbar('Error occurred while saving product', { variant: 'error' });
    }
  };
  const onClose = () => {
    setOpen(false);
  };

  // DIALOG FORM
  const {
    handleSubmit: handleDialogSubmit,
    control: dialogControl,
    reset: dialogReset,
    formState: { errors: dialogErrors },
  } = useForm({
    resolver: yupResolver(dialogSchema),
    defaultValues: { name: '' },
  });

  const addNewUnitType = async ({ name }) => {
    const response = await post('users/create_productType', { name });
    if (response.status == 200) {
      enqueueSnackbar(response.message || 'Product Type Created Successfully', {
        variant: 'success',
      });
      fetchType();
    } else {
      enqueueSnackbar(response.message || 'Error while Creating Product Type', {
        variant: 'error',
      });
    }
    dialogReset();
    setOpen(false);
  };

  return (
    <Paper sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            shape="circle"
            variant="soft"
            color="neutral"
            onClick={() => {
              navigate('/inventory-list');
            }}
            sx={{ mr: 1 }}
          >
            <IconifyIcon icon="material-symbols:arrow-back-rounded" fontSize={20} />
          </Button>
          <Typography variant="h5" gutterBottom>
            Add New Inventory Item
          </Typography>
        </Box>
        <Button variant="soft" shape="square" color="neutral" sx={{ gap: 0.5, p: 1 }}>
          <IconifyIcon icon="material-symbols:add-2-rounded" />
          <Box
            component="span"
            sx={{ display: { xs: 'none', sm: 'block' } }}
            onClick={() => setOpen(!open)}
          >
            Add product type
          </Box>
        </Button>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.type}>
                  <InputLabel>Product Type</InputLabel>
                  <Select {...field} label="Select Product Type">
                    {types.map((t) => (
                      <MenuItem key={t} value={t?._id}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.type?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="productName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Product Name"
                  fullWidth
                  error={!!errors.productName}
                  helperText={errors.productName?.message}
                />
              )}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="hsnCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="HSN Code"
                  fullWidth
                  error={!!errors.hsnCode}
                  helperText={errors.hsnCode?.message}
                />
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="unitType"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Unit Type"
                  fullWidth
                  error={!!errors.unitType}
                  helperText={errors.unitType?.message}
                >
                  {unitTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="minimumQnt"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Minimum Quantity"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0 }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {selectedUnit ? selectedUnit : 'Units'}
                        </InputAdornment>
                      ),
                    },
                    // htmpInput: {
                    //   min: 0,
                    // },
                  }}
                  error={!!errors.minimumQnt}
                  helperText={errors.minimumQnt?.message}
                />
              )}
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="currentQnt"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Current Quantity"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0 }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {selectedUnit ? selectedUnit : 'Units'}
                        </InputAdornment>
                      ),
                    },
                    // htmpInput: {
                    //   min: 0,
                    // },
                  }}
                  error={!!errors.currentQnt}
                  helperText={errors.currentQnt?.message}
                />
              )}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="manufactureName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Manufacturer Name"
                  fullWidth
                  error={!!errors.manufactureName}
                  helperText={errors.manufactureName?.message}
                />
              )}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="vendorName"
              control={control}
              render={({ field }) => (
                // <TextField
                //   {...field}
                //   label="vendorName Name"
                //   fullWidth
                //   error={!!errors.vendorName}
                //   helperText={errors.vendorName?.message}
                // />
                <FormControl fullWidth error={!!errors.type}>
                  <InputLabel>Vendor Name</InputLabel>
                  <Select {...field} label="Select Product Type">
                    {vendors.map((v) => (
                      <MenuItem key={v} value={v?._id}>
                        {v.vendorName}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.vendorName?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="productImage"
              control={control}
              render={({ field }) => (
                <>
                  <Button variant="outlined" component="label" fullWidth sx={{ height: '56px' }}>
                    Upload Image
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        field.onChange(file);
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = () => setPreview(reader.result);
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </Button>
                  {errors.productImage && (
                    <Typography color="error" variant="caption">
                      {errors.productImage?.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Grid>
          {!preview && <Grid item size={{ xs: 12, sm: 6 }}></Grid>}

          {/* {preview && (
            <>
              <Grid
                item
                size={{ xs: 12, sm: 6 }}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}
              >
                <Box
                  component="img"
                  src={preview}
                  alt="Preview"
                  sx={{
                    width: 60,
                    height: 60,
                    objectFit: 'cover',
                    borderRadius: 2,
                  }}
                />
              </Grid>
            </>
          )} */}
          {preview && (
            <Grid
              item
              size={{ xs: 12, sm: 6 }}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start' }}
            >
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  component="img"
                  src={preview}
                  alt="Preview"
                  sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 2 }}
                />
                <Button
                  color="error"
                  size="small"
                  onClick={() => {
                    setPreview(null);
                    setValue('productImage', null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = ''; // ✅ clear file input
                    }
                  }}
                >
                  Remove
                </Button>
              </Box>
            </Grid>
          )}

          <Grid itemsize={{ xs: 12, sm: 6 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: 2, mt: 2 }}
            >
              <Button variant="outlined" onClick={() => reset()}>
                Cancel
              </Button>
              <Button variant="contained" type="submit">
                {isEditMode ? 'Update' : 'Create'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="dialog-title"
        component="form"
        onSubmit={handleDialogSubmit(addNewUnitType)}
        sx={{
          [`& .${dialogClasses.paper}`]: {
            p: 0,
            borderRadius: 6,
            width: 1,
            maxWidth: 600,
          },
        }}
      >
        <Stack sx={{ p: 3, pb: 2, justifyContent: 'space-between', position: 'sticky', top: 0 }}>
          <DialogTitle id="dialog-title" sx={{ p: 0, typography: 'h6' }}>
            Add Product Type
          </DialogTitle>
          <Button
            variant="text"
            size="small"
            color="neutral"
            onClick={onClose}
            sx={{ minWidth: 0, p: 0.5 }}
          >
            <IconifyIcon icon="material-symbols:close-rounded" sx={{ fontSize: 20 }} />
          </Button>
        </Stack>
        <DialogContent sx={{ px: 3, py: 1 }}>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12 }}>
              <Controller
                name="name"
                control={dialogControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Product Type (e.g., DCDB, ACDB, Ac Cable)"
                    fullWidth
                    autoFocus
                    error={!!dialogErrors.newUnitType}
                    helperText={dialogErrors.newUnitType?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button type="submit" variant="contained" autoFocus>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CreateInventory;
